import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  Spinner,
} from '@apollo/orbit';
import { FormikHelpers } from 'formik';
import { groupBy } from 'lodash';
import React, { useCallback, useEffect } from 'react';

import { useSchemaFromRegistry } from 'src/app/graph/explorerPage/hooks/useExplorerState/useSchema/useSchemaFromRegistry/useSchemaFromRegistry';
import { parseGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { API_NAME_MAX_CHAR } from 'src/lib/graphOnboarding/constants';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { InputCharCount } from '../../../../../components/InputCharCount';
import {
  CommonStepProps,
  NewSupergraphOnboardingFields,
  getSubgraphNameFromGraphVariant,
} from '../shared';

export const RegisteredGraphTabContent = ({
  variantsToCreateSupergraphFrom,
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  handleBlur,
  validateField,
  setLoading,
  setValues,
  setSchemaUploaded,
}: {
  variantsToCreateSupergraphFrom: Array<
    GraphQLTypes.NewSupergraphOnboardingQuery_account_graphs_variants & {
      graph: { id: string; title: string };
    }
  >;
  setFieldTouched: FormikHelpers<NewSupergraphOnboardingFields>['setFieldTouched'];
  setSchemaUploaded: React.Dispatch<React.SetStateAction<boolean>>;
} & Omit<
  CommonStepProps,
  | 'loading'
  | 'setContinueDisabled'
  | 'creatingSupergraph'
  | 'continueAnalyticsLabelsByStep'
  | 'setWhenStepsBegan'
  | 'whenStepsBegan'
  | 'createSupergraph'
  | 'createGraphError'
  | 'createGraphValidationError'
>) => {
  const variantsGroupedByGraphTitle = groupBy(
    variantsToCreateSupergraphFrom,
    'graph.title',
  );

  const { schemaDocument, loading: schemaDocumentLoading } =
    useSchemaFromRegistry({
      graphRef: parseGraphRef(values.registeredGraphRef),
      skip: !values.registeredGraphRef,
    });

  useEffect(() => {
    setLoading(schemaDocumentLoading);
  }, [schemaDocumentLoading, setLoading]);

  useEffect(() => {
    // whenever the user selects a reg'd variant and has an uploaded schema,
    // make the schema the registered variant's schema & set the endpointUrl

    if (values.registeredGraphRef && schemaDocument) {
      setFieldValue('sdl', schemaDocument, true);
      setFieldTouched('sdl', true);
      setSchemaUploaded(true);

      const registeredEndpointUrl = variantsToCreateSupergraphFrom.find(
        (variant) => variant.id === values.registeredGraphRef,
      )?.url;
      if (registeredEndpointUrl) {
        setFieldValue('registeredEndpointUrl', registeredEndpointUrl);
        setFieldTouched('registeredEndpointUrl', true);
      }
      setFieldTouched('registeredGraphRef', true);
    }
  }, [
    schemaDocument,
    values,
    setFieldValue,
    setFieldTouched,
    setSchemaUploaded,
    variantsToCreateSupergraphFrom,
  ]);

  // The below 3 useEffects are necessary because we programmatically
  // set the registeredVariantSubgraphName in the above useEffect & we want to validate the form
  // so the user can click 'next' without focusing & unfocusing the registeredVariantSubgraphName field.
  // We need to validate for the user to click next because the validation schema
  // expects a registeredVariantSubgraphName to exist & unless its validated, it doesn't know.
  useEffect(() => {
    if (
      values.registeredVariantSubgraphName.length > 0 &&
      !touched.registeredVariantSubgraphName
    ) {
      validateField('registeredVariantSubgraphName');
    }
  }, [
    touched.registeredVariantSubgraphName,
    validateField,
    values.registeredVariantSubgraphName,
  ]);
  useEffect(() => {
    if (values.sdl.length > 0 && !touched.sdl) {
      validateField('sdl');
    }
  }, [setFieldTouched, touched.sdl, validateField, values.sdl.length]);

  const clearOtherTab = useCallback(() => {
    setFieldValue('unregisteredSubgraphName', '');
    setFieldTouched('unregisteredSubgraphName', false);
    setFieldValue('unregisteredEndpointUrl', '');
    setFieldTouched('unregisteredEndpointUrl', false);
  }, [setFieldTouched, setFieldValue]);

  /**
   * Remove stale errors as fast as possible, validate for every change when there are errors
   *
   * Why are these in a useEffect and not in the `onChange` for the fields, Maya, you may ask?
   * That is because if you validate right after you set the field,
   * we are validating the previous value, not the updated one
   */
  useEffect(() => {
    if (errors.registeredVariantSubgraphName)
      validateField('registeredVariantSubgraphName');
  }, [
    errors.registeredVariantSubgraphName,
    validateField,
    values.registeredVariantSubgraphName,
  ]);

  return (
    <>
      <div className="my-5 text-base text-secondary">
        Point us to a graph variant so we can copy its most recent published
        schema and endpoint.
        <div className="hidden lg:visible">
          We’ll use those as input for the first subgraph in the new supergraph.
          Your registered graph variant will remain unchanged.
        </div>
      </div>
      <FormControl>
        <FormLabel>Registered graphs</FormLabel>
        <Select
          name="selectedRegisteredVariant"
          data-testid="registered-graph-select-trigger"
          onBlur={handleBlur}
          onChange={(e) => {
            clearOtherTab();
            setFieldValue('registeredGraphRef', e.target.value);
            const graphRef = parseGraphRef(e.target.value);
            const graphTitle = variantsToCreateSupergraphFrom.find(
              (variant) => variant.id === e.target.value,
            )?.graph.title;
            // Take a guess & prefill the api name from the chosen graph
            if (graphRef && graphTitle) {
              setFieldValue(
                'registeredVariantSubgraphName',
                getSubgraphNameFromGraphVariant({
                  graphTitle,
                  variantName: graphRef.graphVariant,
                }),
              );
            }
          }}
          value={values.registeredGraphRef}
          placeholder="Select a graph variant"
        >
          {Object.entries(variantsGroupedByGraphTitle).map(
            ([graphTitle, variants]) => (
              <optgroup label={graphTitle} key={graphTitle}>
                {variants.map((variant) => (
                  <option
                    key={variant.id}
                    value={variant.id}
                    data-testid={`option-${variant.name}`}
                  >
                    {variant.name}
                  </option>
                ))}
              </optgroup>
            ),
          )}
        </Select>
      </FormControl>

      {schemaDocumentLoading && values.registeredGraphRef && (
        <div className="mt-2 flex items-center text-sm">
          <Spinner size="tiny" className="mr-2" />
          Grabbing the schema on{' '}
          <span className="ml-1 font-semibold">
            {values.registeredGraphRef}
          </span>
        </div>
      )}
      <FormControl
        className="mt-8 pb-6"
        isInvalid={
          !!errors.registeredVariantSubgraphName &&
          !!touched.registeredVariantSubgraphName
        }
      >
        <FormLabel>
          Subgraph name{' '}
          <Tooltip label="This will be the name of your subgraph. Enter a name that reflects the data or capabilities that your graph provides.">
            <IconInfo data-testid="focusable-info-subgraph-name" tabIndex={0} />
          </Tooltip>
        </FormLabel>
        <Input
          onBlur={handleBlur}
          onChange={(e) => {
            clearOtherTab();
            setValues(
              (fields) => ({
                ...fields,
                registeredVariantSubgraphName: e.target.value,
              }),
              // validate on change only when there are errors
              'registeredVariantSubgraphName' in errors,
            );
          }}
          value={values.registeredVariantSubgraphName}
          name="registeredVariantSubgraphName"
          placeholder="Give your new subgraph a name"
          rightElement={
            <InputCharCount
              count={values.registeredVariantSubgraphName.length}
              maxCount={API_NAME_MAX_CHAR}
            />
          }
        />
        <FormErrorMessage>
          {errors.registeredVariantSubgraphName}
        </FormErrorMessage>
      </FormControl>
    </>
  );
};
